/**
 * Email Verification Page
 *
 * Handles email verification flow:
 * 1. Extracts token from URL query parameter
 * 2. Calls verify-email API endpoint on page load
 * 3. Shows appropriate state: loading, success, or error
 * 4. Auto-redirects to login after successful verification
 */

import * as React from "react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@provenleap/ui";
import {
  LoadingSpinner,
  MailCheckIcon,
  ClockIcon,
  XIcon,
} from "../../components/icons";

interface VerifyEmailPageProps {
  apiBaseUrl?: string;
}

type VerificationState = "loading" | "success" | "expired" | "invalid";

export function VerifyEmailPage({ apiBaseUrl = "" }: VerifyEmailPageProps) {
  const [state, setState] = React.useState<VerificationState>("loading");
  const [countdown, setCountdown] = React.useState(5);
  const hasVerified = React.useRef(false);

  // Extract token from URL
  const token = React.useMemo(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get("token");
    }
    return null;
  }, []);

  // Verify email on mount
  React.useEffect(() => {
    if (hasVerified.current) return;

    const verifyEmail = async () => {
      if (!token) {
        setState("invalid");
        return;
      }

      hasVerified.current = true;

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/auth/verify-email?token=${encodeURIComponent(token)}`,
          { method: "GET" }
        );

        if (response.ok) {
          setState("success");
          return;
        }

        const data = await response.json();

        if (response.status === 410 || data.type?.includes("token-expired")) {
          setState("expired");
        } else {
          setState("invalid");
        }
      } catch {
        setState("invalid");
      }
    };

    verifyEmail();
  }, [token, apiBaseUrl]);

  // Countdown timer for auto-redirect
  React.useEffect(() => {
    if (state !== "success") return;

    if (countdown <= 0) {
      window.location.href = "/login?verified=true";
      return;
    }

    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [state, countdown]);

  const handleGoToLogin = () => {
    window.location.href = "/login?verified=true";
  };

  const handleRequestNewLink = () => {
    window.location.href = "/check-email";
  };

  const handleReturnToLogin = () => {
    window.location.href = "/login";
  };

  return (
    <>
      {state === "loading" && <LoadingState />}
      {state === "success" && (
        <SuccessState countdown={countdown} onGoToLogin={handleGoToLogin} />
      )}
      {state === "expired" && (
        <ExpiredState onRequestNewLink={handleRequestNewLink} />
      )}
      {state === "invalid" && (
        <InvalidState onReturnToLogin={handleReturnToLogin} />
      )}
    </>
  );
}

function LoadingState() {
  return (
    <Card className="w-full text-center !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader>
        <div className="mx-auto mb-4 w-12 h-12 flex items-center justify-center">
          <LoadingSpinner className="h-8 w-8 text-brand" />
        </div>
        <CardTitle>Verifying your email...</CardTitle>
        <CardDescription>Please wait while we verify your email address.</CardDescription>
      </CardHeader>
    </Card>
  );
}

interface SuccessStateProps {
  countdown: number;
  onGoToLogin: () => void;
}

function SuccessState({ countdown, onGoToLogin }: SuccessStateProps) {
  return (
    <Card className="w-full text-center !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader>
        <MailCheckIcon
          className="mx-auto mb-4 text-foreground"
          size={30}
          checkColor="#22c55e"
          autoAnimate
        />
        <CardTitle>Your email has been verified!</CardTitle>
        <CardDescription className="mt-2 text-neutral-700">
          Redirecting to login in {countdown} seconds...
        </CardDescription>
      </CardHeader>
      <CardFooter className="justify-center">
        <Button onClick={onGoToLogin} className="cursor-pointer">
          Go to login now
        </Button>
      </CardFooter>
    </Card>
  );
}

interface ExpiredStateProps {
  onRequestNewLink: () => void;
}

function ExpiredState({ onRequestNewLink }: ExpiredStateProps) {
  return (
    <Card className="w-full text-center !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader>
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
          <ClockIcon className="h-6 w-6 text-warning" />
        </div>
        <CardTitle>This verification link has expired</CardTitle>
        <CardDescription>
          Verification links are valid for 24 hours. Please request a new one.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Don't worry, you can easily request a new verification email.
        </p>
      </CardContent>
      <CardFooter className="justify-center">
        <Button onClick={onRequestNewLink} className="cursor-pointer">Request new verification email</Button>
      </CardFooter>
    </Card>
  );
}

interface InvalidStateProps {
  onReturnToLogin: () => void;
}

function InvalidState({ onReturnToLogin }: InvalidStateProps) {
  return (
    <Card className="w-full text-center !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader>
        <XIcon className="mx-auto mb-4 h-8 w-8 text-error" />
        <CardTitle>This verification link is invalid</CardTitle>
        <CardDescription className="mt-2 text-neutral-700">
          The link may have already been used or is incorrect.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-neutral-700">
          If you've already verified your email, you can proceed to login.
          Otherwise, try requesting a new verification email.
        </p>
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button onClick={onReturnToLogin} className="w-full cursor-pointer">
          Return to login
        </Button>
        <a
          href="/check-email"
          className="mt-2 text-sm font-medium text-primary hover:underline"
        >
          Request new verification email
        </a>
      </CardFooter>
    </Card>
  );
}

